import {GeocodeService} from '@scandium-oy/ngx-scandium';
import {resolveEmployeeCoordinates, validEmployeeCoordinates} from '../employee-address';
import { EmployeeSchedulesComponent } from './employee-schedules.component';
import { EmployeeWorkforceComponent } from '../../workforce-v2/employee-workforce.component';
import { WorkforceService } from '../../workforce-v2/workforce.service';
import { Schedule } from '../../models/schedule.model';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { Browser } from '@capacitor/browser';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { addDays, addWeeks, differenceInMinutes, eachDayOfInterval, endOfToday, endOfWeek, format, isToday, isWeekend, startOfToday, startOfWeek, subWeeks } from 'date-fns';
import { fi } from 'date-fns/locale';
import { combineLatest, defer, firstValueFrom, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SegmentButton, SegmentListComponent } from 'src/app/components/segment-list/segment-list.component';
import { IAttachment } from 'src/app/models/attachment.model';
import { OptionsPaycheckItem } from 'src/app/models/options.model';
import { Tag } from 'src/app/models/tag.model';
import { Timesheet } from 'src/app/models/timesheet.model';
import { User } from 'src/app/models/user.model';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { NetvisorService } from 'src/app/services/netvisor.service';
import { PaychecksService } from 'src/app/services/paychecks.service';
import { ProcountorService } from 'src/app/services/procountor.service';
import { SitesService } from 'src/app/services/sites.service';
import { TagsService } from 'src/app/services/tags.service';
import { TimesheetsService } from 'src/app/services/timesheets.service';
import { UiFeedbackService } from 'src/app/services/ui-feedback.service';
import { UsersService } from 'src/app/services/users.service';
import { AddAttachmentDialogComponent } from 'src/app/sites/site/components/attachments/dialog/add-attachment.dialog';
import { paymentTerms, tes, userDocuments, workTypes } from 'src/app/utility/constants';
import { Roles } from 'src/app/utility/role';
import { fieldSorter } from 'src/app/utility/sort';
import { convertTimestamp, isTimestamp } from 'src/app/utility/time';
import { getDurations } from 'src/app/utility/timesheet';

interface EmployeeShiftItem {
  date: string;
  siteName: string;
  start?: Date | Timestamp;
  end?: Date | Timestamp;
}

interface EmployeeAssignmentItem {
  guid: string;
  name: string;
  upcomingCount: number;
}

interface EmployeeOverviewStat {
  label: string;
  value: string;
  detail: string;
}

interface EmployeeActivityItem {
  title: string;
  detail: string;
  meta: string;
}

interface EmployeeShiftDetail {
  id: string;
  scheduleGuid: string;
  siteGuid: string | null;
  date: Date;
  start: Date | null;
  end: Date | null;
  dateLabel: string;
  timeLabel: string;
  durationLabel: string;
  siteName: string;
  clientName: string;
  teamLabel: string;
  workTypeLabel: string;
  statusLabel: 'active' | 'upcoming' | 'ended';
  statusLabelText: string;
  calendarType: 'shift' | 'allocation' | 'absence';
  calendarTypeLabel: string;
  absenceLabel: string | null;
  notes: string;
}

interface EmployeeAllocationVm {
  current: EmployeeShiftDetail | null;
  upcoming: EmployeeShiftDetail[];
  history: EmployeeShiftDetail[];
  overlaps: EmployeeShiftDetail[][];
}

interface EmployeeCalendarDay {
  iso: string;
  date: Date;
  label: string;
  shortDate: string;
  isToday: boolean;
  isWeekend: boolean;
  isCurrentMonth: boolean;
  items: EmployeeShiftDetail[];
}

interface EmployeeCalendarVm {
  periodLabel: string;
  days: EmployeeCalendarDay[];
  shiftsCount: number;
  activeAllocationCount: number;
  absenceCount: number;
  conflictCount: number;
  hasAnyItems: boolean;
}

interface EmployeePayrollPeriodSummary {
  label: string;
  start: Date;
  end: Date;
  totalHours: number;
  approvedHours: number;
  normalHours: number;
  estimatedPay: number | null;
  missingEntries: number;
  absences: number;
  overtimeHours: number;
  status: 'ready' | 'pending' | 'incomplete';
  statusLabel: string;
}

interface EmployeePayrollIssue {
  title: string;
  detail: string;
}

interface EmployeePayrollVm {
  current: EmployeePayrollPeriodSummary | null;
  history: EmployeePayrollPeriodSummary[];
  issues: EmployeePayrollIssue[];
}

interface EmployeeContractVm {
  workerType: 'employee' | 'subcontractor';
  hourlyWage: number | null;
  socialCostMultiplier: number | null;
  collectiveAgreement: string | null;
  employmentType: 'full_time' | 'zero_to_forty' | null;
  billingRate: number | null;
  currency: string;
  vatIncluded: boolean;
  usesMockData: boolean;
}

interface BasicInfoDraft {
  displayName: string;
  email: string;
  phone: string;
  role: string;
}

interface OrganizationDraft {
  team: string;
  area: string;
  company: string;
}

interface EmploymentDraft {
  salaryOrInvoice: 'salary' | 'invoice' | '';
  workType: '40h' | 'noSelection' | 'fullTime' | '';
  hourlyRate: number | null;
  sideCost: number | null;
  tes: string;
  paymentTerms: string;
  inactive: boolean;
}

interface ExtraInfoDraft {
  languages: string;
  street: string;
  postalCode: string;
  city: string;
}

interface EmployeeDocumentItem {
  key: string;
  name: string;
  category: 'contracts' | 'certificates' | 'identity' | 'other';
  uploadedDate: string;
  status: 'valid' | 'missing';
  uploader: string;
  url: string | null;
  required: boolean;
}

interface EmployeeDocumentsVm {
  keyDocuments: EmployeeDocumentItem[];
  otherDocuments: EmployeeDocumentItem[];
  missingDocuments: EmployeeDocumentItem[];
}

interface EmployeeWorksiteLinkItem {
  guid: string;
  name: string;
  upcomingCount: number;
}

interface EmployeeWorksiteSelectItem {
  guid: string;
  name: string;
  selected?: boolean;
}

const EMPTY_ALLOCATION_VM: EmployeeAllocationVm = {
  current: null,
  upcoming: [],
  history: [],
  overlaps: [],
};

const EMPTY_CALENDAR_VM: EmployeeCalendarVm = {
  periodLabel: '',
  days: [],
  shiftsCount: 0,
  activeAllocationCount: 0,
  absenceCount: 0,
  conflictCount: 0,
  hasAnyItems: false,
};

const DEFAULT_SOCIAL_COST_MULTIPLIER = 1.2;
const DEFAULT_CURRENCY = 'EUR';

@Component({
  selector: 'app-employee-workspace-dialog',
  templateUrl: './employee-workspace.dialog.html',
  styleUrls: ['./employee-workspace.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AppCommonModule,
    EmployeeWorkforceComponent,
    EmployeeSchedulesComponent,
    SegmentListComponent,
  ],
})
export class EmployeeWorkspaceDialogComponent {
  private modalCtrl = inject(ModalController);
  private feedback = inject(UiFeedbackService);
  private translate = inject(TranslateService);
  private contractorsService = inject(ContractorsService);
  private navigationService = inject(NavigationService);
  private netvisorService = inject(NetvisorService);
  private paychecksService = inject(PaychecksService);
  private procountorService = inject(ProcountorService);
  private usersService = inject(UsersService);
  private workforceApi = inject(WorkforceService);
  private sitesService = inject(SitesService);
  private tagsService = inject(TagsService);
  private timesheetsService = inject(TimesheetsService);

  user = input.required<User>();
  initialTab = input<string | null>(null);
  initialShiftDate = input<Date | null>(null);
  initialShiftSiteGuid = input<string | null>(null);

  workspaceTab = signal('overview');
  calendarAnchor = signal(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
  selectedCalendarItemId = signal<string | null>(null);
  schedulesReload = signal(0);
  basicEdit = signal(false);
  organizationEdit = signal(false);
  employmentEdit = signal(false);
  extraEdit = signal(false);
  private addressGeocoder = inject(GeocodeService);
  coordinatesUnavailable = computed(() => {
    this.userRefresh();
    const user = this.user();
    return !!user?.address?.street && !validEmployeeCoordinates(user.location);
  });

  settingsError = signal('');
  private userRefresh = signal(0);
  private worksiteLinksRefresh = signal(0);
  basicDraft = signal<BasicInfoDraft>({ displayName: '', email: '', phone: '', role: '' });
  organizationDraft = signal<OrganizationDraft>({ team: '', area: '', company: '' });
  employmentDraft = signal<EmploymentDraft>({
    salaryOrInvoice: '',
    workType: '',
    hourlyRate: null,
    sideCost: null,
    tes: '',
    paymentTerms: '',
    inactive: false,
  });

  hourlyRate24 = signal('');
  extraDraft = signal<ExtraInfoDraft>({ languages: '', street: '', postalCode: '', city: '' });
  integrationDraft = signal<{ netvisorEmployeeNumber: string | null; procountorEmployeeNumber: number | null }>({
    netvisorEmployeeNumber: null,
    procountorEmployeeNumber: null,
  });

  hasNetvisor = computed(() => !!this.contractorsService.contractorS()?.integrations?.netvisor);
  hasProcountor = computed(() => !!this.contractorsService.contractorS()?.integrations?.procountor);

  netvisorEmployees$ = this.netvisorService.getEmployees().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  procountorEmployees$ = this.procountorService.getEmployees().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  workspaceTabs = computed<SegmentButton[]>(() => [
    {value:'overview', name:this.translate.instant('users.workspace.tabs.overview'), show:true},
    {value:'schedule', name:this.translate.instant('users.workspace.tabs.schedule'), show:true},
    {value:'time', name:this.translate.instant('workforceV2.work-days'), show:true},
    {value:'documents', name:this.translate.instant('users.workspace.tabs.documents'), show:true},
    {value:'settings', name:this.translate.instant('users.workspace.tabs.settings'), show:true},
  ]);

  currentTabLabel = computed(() => this.workspaceTabs().find((tab) => tab.value === this.workspaceTab())?.name || '');
  readonly tesOptions = tes;
  readonly workTypeOptions = workTypes;
  readonly paymentTermOptions = paymentTerms;

  roleLabel = computed(() => this.user()?.role ? this.translate.instant('users.roles.' + this.user().role) : this.translate.instant('users.workspace.fallbacks.noRole'));
  personTypeLabel = computed(() => {
    const user = this.user();
    if (user?.salaryOrInvoice === 'invoice' || (user?.subcontractors?.length ?? 0) > 0) {
      return this.translate.instant('users.workspace.personType.subcontractor');
    }
    return this.translate.instant('users.workspace.personType.employee');
  });

  statusLabel = computed(() => {
    const user = this.user();
    if (!user) {
      return '';
    }
    if (user.disabled) {
      return this.translate.instant('users.status.disabled');
    }
    if (user.inactive) {
      return this.translate.instant('users.status.inactive');
    }
    return this.translate.instant('users.status.active');
  });

  emailLabel = computed(() => this.user()?.email || this.translate.instant('users.workspace.fallbacks.noEmail'));
  phoneLabel = computed(() => this.user()?.phone || this.translate.instant('users.workspace.fallbacks.noPhone'));
  displayNameLabel = computed(() => this.formatValue(this.user()?.displayName, this.translate.instant('users.workspace.fallbacks.noName')));
  teamLabel = computed(() => this.formatValue(this.teamLabelValue(), this.translate.instant('users.workspace.fallbacks.noTeam')));
  areaLabel = computed(() => this.formatValue(this.areaLabelValue(), this.translate.instant('users.workspace.fallbacks.noArea')));
  companyLabel = computed(() => this.formatValue(this.user()?.company, this.translate.instant('users.workspace.fallbacks.noCompany')));
  languagesLabel = computed(() => this.formatValue(this.user()?.languages?.join(', '), this.translate.instant('users.workspace.settings.fallbacks.noLanguages')));
  streetLabel = computed(() => this.formatValue(this.user()?.address?.street, this.translate.instant('users.workspace.settings.fallbacks.noStreet')));
  postalCodeLabel = computed(() => this.formatValue(this.user()?.address?.postalCode, this.translate.instant('users.workspace.settings.fallbacks.noPostalCode')));
  cityLabel = computed(() => this.formatValue(this.user()?.address?.city, this.translate.instant('users.workspace.settings.fallbacks.noCity')));
  employmentTypeText = computed(() => this.getEmploymentTypeLabel(this.user().salaryOrInvoice));
  workerTypeText = computed(() => this.getWorkerTypeLabel(this.user().workType));

  private upcomingSchedules$ = combineLatest([toObservable(this.user), toObservable(this.schedulesReload)]).pipe(
    switchMap(([user])=>defer(async ()=>{
      const start = format(startOfToday(), 'yyyy-MM-dd'), end = format(addDays(startOfToday(), 90), 'yyyy-MM-dd');
      const rows = await this.workforceApi.request<{id: string;shift_date: string;firestore_user_id: string;firestore_site_id: string;start_time: string;end_time: string;status: 'draft' | 'published';cancelled: boolean}[]>(`shifts?start=${start}&end=${end}&published=true`);
      return rows.filter(r=>r.firestore_user_id === user.guid && !r.cancelled).map((r): Schedule=>({guid:r.id, contractor:'', date:new Date(r.shift_date + 'T12:00:00').toLocaleDateString('fi'), dateObj:new Date(r.shift_date + 'T12:00:00'), users:[r.firestore_user_id], site:r.firestore_site_id, machines:[], machinesGuids:[], projects:[], times:[{user:r.firestore_user_id, start:r.start_time ? new Date(r.shift_date + 'T' + r.start_time) : null, end:r.end_time ? new Date(r.shift_date + 'T' + r.end_time) : null, status:r.status}]}));
    })), shareReplay({bufferSize:1, refCount:true}),
  );

  private worksites$ = combineLatest([toObservable(this.user), toObservable(this.worksiteLinksRefresh)]).pipe(
    switchMap(([user])=>defer(async ()=>this.workforceApi.request<{firestore_user_id: string;firestore_site_id: string}[]>('employee-site-links')).pipe(map(links=>links.filter(l=>l.firestore_user_id === user.guid).map(l=>l.firestore_site_id)))),
    switchMap(ids=>ids.length ? this.sitesService.getByGuids(ids) : of([])), shareReplay({bufferSize:1, refCount:true}),
  );

  linkedWorksites$ = combineLatest([this.worksites$, this.upcomingSchedules$]).pipe(
    map(([sites, schedules]) => sites.map((site): EmployeeWorksiteLinkItem => ({
      guid: site.guid,
      name: site.name,
      upcomingCount: schedules.filter((schedule) => schedule.site === site.guid).length,
    }))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  availableWorksitesToLink$ = combineLatest([
    this.sitesService.getAdminList(true),
    this.worksites$,
  ]).pipe(
    map(([sites, linkedSites]): EmployeeWorksiteSelectItem[] => {
      const linked = new Set(linkedSites.map((site) => site.guid));
      return sites
        .filter((site) => !linked.has(site.guid))
        .sort(fieldSorter(['name']))
        .map((site): EmployeeWorksiteSelectItem => ({ guid: site.guid, name: site.name, selected: false }));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  siteLinkMetrics$ = combineLatest([this.linkedWorksites$, this.availableWorksitesToLink$]).pipe(
    map(([linkedSites, availableSites]) => ({
      linked: linkedSites.length,
      available: availableSites.length,
      upcoming: linkedSites
        .map((site) => site.upcomingCount ?? 0)
        .reduce((sum, count) => sum + count, 0),
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private employeeTags$ = toObservable(this.user).pipe(
    switchMap((user) => {
      const contractorGuid = user?.contractors?.[0] ?? this.contractorsService.contractorS()?.guid;
      if (!user?.guid || !contractorGuid) {
        return of([]);
      }
      return this.tagsService.getList({ contractor: contractorGuid }).pipe(
        map((tags) => tags.filter((tag) => tag.users?.some((tagUser) => tagUser.guid === user.guid))),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  teamLabel$ = this.employeeTags$.pipe(
    map((tags) => tags.filter((tag) => tag.type === 'team').map((tag) => tag.name).join(', ')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  areaLabel$ = this.employeeTags$.pipe(
    map((tags) => tags.filter((tag) => tag.type === 'area').map((tag) => tag.name).join(', ')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  teamLabelValue = toSignal(this.teamLabel$, { initialValue: '' });
  areaLabelValue = toSignal(this.areaLabel$, { initialValue: '' });

  private contractorTags$ = toObservable(this.user).pipe(
    switchMap((user) => {
      const contractorGuid = user?.contractors?.[0] ?? this.contractorsService.contractorS()?.guid;
      if (!contractorGuid) {
        return of([] as Tag[]);
      }
      return this.tagsService.getList({ contractor: contractorGuid });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  teamOptions$ = this.contractorTags$.pipe(
    map((tags) => tags.filter((tag) => tag.type === 'team').map((tag) => tag.name).sort()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  areaOptions$ = this.contractorTags$.pipe(
    map((tags) => tags.filter((tag) => tag.type === 'area').map((tag) => tag.name).sort()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  assignmentRows$ = combineLatest([this.worksites$, this.upcomingSchedules$]).pipe(
    map(([sites, schedules]) => sites.map((site) => ({
      guid: site.guid,
      name: site.name,
      upcomingCount: schedules.filter((schedule) => schedule.site === site.guid).length,
    } satisfies EmployeeAssignmentItem))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  nextShift$ = defer(() => this.upcomingShiftRows$).pipe(
    map((items) => items.find((item) => {
      const shiftDate = convertTimestamp(item.start ?? item.end);
      return shiftDate instanceof Date && shiftDate >= startOfToday();
    }) ?? null),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  nextShiftDisplay$ = this.nextShift$.pipe(
    map((nextShift) => nextShift
      ? { ...nextShift, timeRangeLabel: `${this.formatShiftTime(nextShift.start)} - ${this.formatShiftTime(nextShift.end)}` }
      : null),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  currentAssignment$ = this.assignmentRows$.pipe(
    map((items) => items.find((item) => item.upcomingCount > 0) ?? items[0] ?? null),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  thisWeekHours$ = this.upcomingSchedules$.pipe(
    map((schedules) => {
      const weekLimit = addDays(startOfToday(), 7);
      const ms = schedules
        .filter((schedule) => schedule.dateObj instanceof Date ? schedule.dateObj < weekLimit : true)
        .reduce((total, schedule) => {
          const time = schedule.times?.find((entry) => entry.user === this.user().guid);
          const start = time?.start;
          const end = time?.end;
          if (isTimestamp(start) && isTimestamp(end)) {
            return total + (end.toDate().getTime() - start.toDate().getTime());
          }
          if (start instanceof Date && end instanceof Date) {
            return total + (end.getTime() - start.getTime());
          }
          return total;
        }, 0);
      return Math.max(Math.round((ms / 36e5) * 10) / 10, 0);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  overviewStats$ = combineLatest([defer(() => this.upcomingShiftRows$), this.assignmentRows$, this.thisWeekHours$]).pipe(
    map(([shifts, assignments, thisWeekHours]) => {
      const activeAssignments = assignments.filter((item) => item.upcomingCount > 0).length;
      const nextShift = shifts[0];
      return [
        {
          label: this.translate.instant('users.workspace.overview.stats.weekShifts.label'),
          value: String(shifts.length),
          detail: shifts.length > 0
            ? this.translate.instant('users.workspace.overview.stats.weekShifts.detail')
            : this.translate.instant('users.workspace.overview.stats.weekShifts.empty'),
        },
        {
          label: this.translate.instant('users.workspace.overview.stats.activeAssignments.label'),
          value: String(activeAssignments),
          detail: activeAssignments > 0
            ? this.translate.instant('users.workspace.overview.stats.activeAssignments.detail')
            : this.translate.instant('users.workspace.overview.stats.activeAssignments.empty'),
        },
        {
          label: this.translate.instant('users.workspace.overview.stats.weekHours.label'),
          value: `${thisWeekHours} h`,
          detail: thisWeekHours > 0
            ? this.translate.instant('users.workspace.overview.stats.weekHours.detail')
            : this.translate.instant('users.workspace.overview.stats.weekHours.empty'),
        },
        {
          label: this.translate.instant('users.workspace.overview.stats.nextShift.label'),
          value: nextShift ? nextShift.date : this.translate.instant('users.workspace.fallbacks.noShift'),
          detail: nextShift ? nextShift.siteName : this.translate.instant('users.workspace.fallbacks.noScheduledShifts'),
        },
      ] satisfies EmployeeOverviewStat[];
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  recentActivity$ = combineLatest([toObservable(this.user), this.nextShift$, this.currentAssignment$]).pipe(
    map(([user, nextShift, currentAssignment]) => {
      const items: EmployeeActivityItem[] = [];
      if (nextShift) {
        items.push({
          title: this.translate.instant('users.workspace.activity.nextShift.title'),
          detail: [String(nextShift.siteName ?? ''), String(nextShift.date ?? '')].join(' · '),
          meta: nextShift.start || nextShift.end
            ? this.formatShiftTime(nextShift.start) + ' - ' + this.formatShiftTime(nextShift.end)
            : this.translate.instant('users.workspace.fallbacks.noDetails'),
        });
      }
      if (currentAssignment) {
        items.push({
          title: this.translate.instant('users.workspace.activity.currentAssignment.title'),
          detail: currentAssignment.name,
          meta: currentAssignment.upcomingCount > 0
            ? String(currentAssignment.upcomingCount) + ' ' + String(this.translate.instant('users.workspace.fallbacks.upcomingShifts'))
            : this.translate.instant('users.workspace.fallbacks.noUpcomingShifts'),
        });
      }
      if (user?.creationDate || user?.creationTime) {
        const createdAt = user.creationDate instanceof Date
          ? format(user.creationDate, 'd.M.yyyy')
          : user.creationTime;
        items.push({
          title: this.translate.instant('users.workspace.activity.created.title'),
          detail: this.translate.instant('users.workspace.activity.created.detail'),
          meta: createdAt || this.translate.instant('users.workspace.fallbacks.noDate'),
        });
      }
      return items.slice(0, 4);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private shiftDetails$ = combineLatest([this.upcomingSchedules$, this.teamLabel$, this.areaLabel$, this.worksites$]).pipe(
    map(([schedules, teamLabel, areaLabel, worksites]) => schedules.flatMap((schedule) => {
      const time = schedule.times?.find((entry) => entry.user === this.user().guid);
      const start = isTimestamp(time?.start) ? time.start.toDate() : time?.start;
      const end = isTimestamp(time?.end) ? time.end.toDate() : time?.end;
      const dateObj = schedule.dateObj instanceof Date ? schedule.dateObj : schedule.dateObj?.toDate?.();
      if (!(dateObj instanceof Date)) {
        return [];
      }

      const site = this.sitesService.getSync(schedule.site) ?? worksites.find((item) => item.guid === schedule.site);
      const durationMinutes = start instanceof Date && end instanceof Date ? Math.max(differenceInMinutes(end, start), 0) : 0;
      const durationHours = durationMinutes > 0 ? Math.round((durationMinutes / 60) * 10) / 10 : 0;
      const statusLabel = this.getShiftStatus(dateObj);
      const calendarType: EmployeeShiftDetail['calendarType'] = time?.absence
        ? 'absence'
        : start instanceof Date && end instanceof Date
          ? 'shift'
          : 'allocation';
      const absenceLabel = time?.absence || (schedule.absence ? this.translate.instant('users.workspace.calendar.item.absence') : null);
      const scheduleGuid = String(schedule.guid ?? '');
      const shiftTimeKey = start instanceof Date ? start.getTime() : dateObj.getTime();

      return [{
        id: scheduleGuid + '-' + String(shiftTimeKey),
        scheduleGuid: schedule.guid,
        siteGuid: site?.guid ?? schedule.site ?? null,
        date: dateObj,
        start: start instanceof Date ? start : null,
        end: end instanceof Date ? end : null,
        dateLabel: schedule.date || format(dateObj, 'd.M.yyyy'),
        timeLabel: start instanceof Date && end instanceof Date
          ? `${this.formatShiftTime(start)} - ${this.formatShiftTime(end)}`
          : this.translate.instant('users.workspace.fallbacks.allDay'),
        durationLabel: durationHours > 0 ? `${durationHours} h` : this.translate.instant('users.workspace.fallbacks.missingDuration'),
        siteName: site?.name || schedule.site || this.translate.instant('users.workspace.fallbacks.missingSite'),
        clientName: site?.clientName || site?.client || this.translate.instant('users.workspace.fallbacks.noClient'),
        teamLabel: teamLabel || this.translate.instant('users.workspace.fallbacks.noTeam'),
        workTypeLabel: areaLabel || this.translate.instant('users.workspace.fallbacks.noArea'),
        statusLabel,
        statusLabelText: this.getShiftStatusLabel(statusLabel),
        calendarType,
        calendarTypeLabel: this.getCalendarTypeLabelByValues(calendarType, absenceLabel),
        absenceLabel,
        notes: schedule.information?.find((info) => info.user === this.user().guid)?.text || this.translate.instant('users.workspace.fallbacks.noDetails'),
      } satisfies EmployeeShiftDetail];
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private employeeShiftDetails$ = this.shiftDetails$.pipe(
    map((items) => items.filter((item) => item.calendarType === 'shift')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  upcomingShiftRows$ = this.employeeShiftDetails$.pipe(
    map((items) => items
      .filter((shift) => shift.date >= startOfToday())
      .map((shift) => ({
        date: shift.dateLabel,
        siteName: shift.siteName,
        start: shift.start ?? null,
        end: shift.end ?? null,
      } satisfies EmployeeShiftItem))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private employeeAllocationDetails$ = this.shiftDetails$.pipe(
    map((items) => items.filter((item) => item.calendarType === 'allocation')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private firstCalendarDate$ = this.shiftDetails$.pipe(
    map((items) => items.find((item) => item.date >= startOfToday())?.date ?? items[0]?.date ?? null),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  allocationVm$ = this.employeeAllocationDetails$.pipe(
    map((allocations) => {
      const today = startOfToday();
      const current = allocations.find((shift) => format(shift.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) ?? null;
      const upcoming = allocations
        .filter((shift) => shift.date > today)
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .slice(0, 8);
      const history = allocations
        .filter((shift) => shift.date < today)
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .slice(0, 8);
      const overlaps = this.findOverlaps(allocations);
      return {
        current,
        upcoming,
        history,
        overlaps,
      } satisfies EmployeeAllocationVm;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private currentPayrollPeriod$ = toObservable(this.user).pipe(
    map((user) => {
      const contractor = this.contractorsService.contractorS();
      if (!user || !contractor) {
        return null;
      }
      const period = this.paychecksService.getCurrentPeriod(contractor.paycheck);
      return this.paychecksService.getDatesByPeriod(period, startOfToday(), user) as OptionsPaycheckItem;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private currentPayrollTimesheets$ = combineLatest([toObservable(this.user), this.currentPayrollPeriod$]).pipe(
    switchMap(([user, period]) => {
      if (!user?.guid || !period) {
        return of([] as Timesheet[]);
      }
      return this.timesheetsService.getListOnce({
        user: user.guid,
        startDate: period.start,
        endDate: period.end,
        contractor: user.contractors?.[0],
      }, true);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private payrollHistoryPeriods$ = toObservable(this.user).pipe(
    map((user) => {
      const contractor = this.contractorsService.contractorS();
      if (!user || !contractor) {
        return [];
      }
      return this.paychecksService.getPastPaymentChecks(contractor, user, 5).slice(0, 5);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private payrollHistory$ = combineLatest([toObservable(this.user), this.payrollHistoryPeriods$]).pipe(
    switchMap(([user, periods]) => {
      if (!user?.guid || periods.length === 0) {
        return of([] as EmployeePayrollPeriodSummary[]);
      }
      return combineLatest(periods.map((period) => this.timesheetsService.getListOnce({
        user: user.guid,
        startDate: period.start,
        endDate: period.end,
        contractor: user.contractors?.[0],
      }, true).pipe(
        map((timesheets) => this.mapPayrollPeriodSummary(period, timesheets)),
      )));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  payrollVm$ = combineLatest([this.currentPayrollPeriod$, this.currentPayrollTimesheets$, this.payrollHistory$]).pipe(
    map(([currentPeriod, currentTimesheets, history]) => {
      const current = currentPeriod ? this.mapPayrollPeriodSummary(currentPeriod, currentTimesheets) : null;
      const issues: EmployeePayrollIssue[] = [];
      if (current?.missingEntries) {
        issues.push({
          title: this.translate.instant('users.workspace.payroll.issues.missingEntries.title'),
          detail: this.translate.instant('users.workspace.payroll.issues.missingEntries.detail', { count: current.missingEntries }),
        });
      }
      if (current?.totalHours === 0) {
        issues.push({
          title: this.translate.instant('users.workspace.payroll.issues.noHours.title'),
          detail: this.translate.instant('users.workspace.payroll.issues.noHours.detail'),
        });
      }
      if (current?.status === 'pending') {
        issues.push({
          title: this.translate.instant('users.workspace.payroll.issues.pendingApproval.title'),
          detail: this.translate.instant('users.workspace.payroll.issues.pendingApproval.detail'),
        });
      }
      return {
        current,
        history,
        issues,
      } satisfies EmployeePayrollVm;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  contractVm = computed<EmployeeContractVm>(() => {
    this.userRefresh();
    const user = this.user();
    const workerType: EmployeeContractVm['workerType'] = user.salaryOrInvoice === 'invoice' || (user.subcontractors?.length ?? 0) > 0
      ? 'subcontractor'
      : 'employee';

    // TODO connect backend field(s) for subcontractor billing metadata.
    return {
      workerType,
      hourlyWage: workerType === 'employee' ? user.hourlyRate ?? null : null,
      socialCostMultiplier: workerType === 'employee' ? user.sideCost ?? DEFAULT_SOCIAL_COST_MULTIPLIER : null,
      collectiveAgreement: workerType === 'employee' ? user.tes ?? null : null,
      employmentType: workerType === 'employee'
        ? user.workType === 'fullTime' ? 'full_time' : user.workType === '40h' ? 'zero_to_forty' : null
        : null,
      billingRate: workerType === 'subcontractor' ? user.hourlyRate ?? null : null,
      currency: DEFAULT_CURRENCY,
      vatIncluded: false,
      usesMockData: workerType === 'subcontractor',
    };
  });

  documentsVm = computed<EmployeeDocumentsVm>(() => {
    const attachments = this.user().attachments ?? [];
    const requiredKeys = userDocuments;
    const requiredDocuments = requiredKeys.map((key) => {
      const attachment = attachments.find((item) => item.name === key);
      return this.mapDocumentItem(key, attachment, true);
    });
    const customDocuments = attachments
      .filter((attachment) => !requiredKeys.includes(attachment.name))
      .map((attachment) => this.mapDocumentItem(attachment.name, attachment, false));

    return {
      keyDocuments: requiredDocuments,
      otherDocuments: customDocuments,
      missingDocuments: requiredDocuments.filter((document) => document.status === 'missing'),
    };
  });

  calendarVm$ = combineLatest([this.shiftDetails$, toObservable(this.calendarAnchor)]).pipe(
    map(([items, anchor]) => {
      const visibleStart = startOfWeek(anchor, { weekStartsOn: 1 });
      const visibleEnd = endOfWeek(anchor, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: visibleStart, end: visibleEnd }).map((date) => {
        const dayItems = items.filter((item) => format(item.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'));
        return {
          iso: format(date, 'yyyy-MM-dd'),
          date,
          label: format(date, 'EEE', { locale: fi }),
          shortDate: format(date, 'd.M.'),
          isToday: isToday(date),
          isWeekend: isWeekend(date),
          isCurrentMonth: true as boolean,
          items: dayItems,
        } satisfies EmployeeCalendarDay;
      });
      const visibleItems = days.flatMap((day) => day.items);
      const overlaps = this.findOverlaps(visibleItems);
      return {
        periodLabel: `${format(visibleStart, 'd.M.', { locale: fi })} - ${format(visibleEnd, 'd.M.yyyy', { locale: fi })}`,
        days,
        shiftsCount: visibleItems.filter((item) => item.calendarType === 'shift').length,
        activeAllocationCount: visibleItems.filter((item) => item.calendarType === 'allocation').length,
        absenceCount: visibleItems.filter((item) => item.calendarType === 'absence').length,
        conflictCount: overlaps.length,
        hasAnyItems: visibleItems.length > 0,
      } satisfies EmployeeCalendarVm;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  selectedCalendarItem$ = combineLatest([this.shiftDetails$, toObservable(this.selectedCalendarItemId), toObservable(this.calendarAnchor)]).pipe(
    map(([items, selectedId, anchor]) => {
      if (selectedId) {
        return items.find((item) => item.id === selectedId) ?? null;
      }
      const visibleStart = startOfWeek(anchor, { weekStartsOn: 1 });
      const visibleEnd = endOfWeek(anchor, { weekStartsOn: 1 });
      return items.find((item) => item.date >= visibleStart && item.date <= visibleEnd) ?? null;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  shiftDetails = toSignal(this.shiftDetails$, { initialValue: [] as EmployeeShiftDetail[] });
  allocationVm = toSignal(this.allocationVm$, { initialValue: EMPTY_ALLOCATION_VM });
  calendarVm = toSignal(this.calendarVm$, { initialValue: EMPTY_CALENDAR_VM });
  selectedCalendarItem = toSignal(this.selectedCalendarItem$, { initialValue: null });
  firstCalendarDate = toSignal(this.firstCalendarDate$, { initialValue: null });
  initialShiftContextApplied = signal(false);

  constructor() {
    effect(() => {
      const user = this.user();
      this.integrationDraft.set({
        netvisorEmployeeNumber: user?.netvisorEmployeeNumber ?? null,
        procountorEmployeeNumber: user?.procountorEmployeeNumber ?? null,
      });
    });

    effect(() => {
      const initialTab = this.initialTab();
      if (initialTab) {
        this.workspaceTab.set(['calendar', 'assignments', 'sites'].includes(initialTab) ? 'schedule' : initialTab === 'payroll' ? 'time' : initialTab);
      }
    });

    effect(() => {
      const initialShiftDate = this.initialShiftDate();
      if (!initialShiftDate) {
        return;
      }
      this.calendarAnchor.set(startOfWeek(initialShiftDate, { weekStartsOn: 1 }));
    });

    effect(() => {
      if (this.initialShiftContextApplied()) {
        return;
      }

      const initialShiftDate = this.initialShiftDate();
      if (!initialShiftDate) {
        this.initialShiftContextApplied.set(true);
        return;
      }

      const siteGuid = this.initialShiftSiteGuid();
      const matchingShift = this.shiftDetails().find((shift) =>
        format(shift.date, 'yyyy-MM-dd') === format(initialShiftDate, 'yyyy-MM-dd')
        && (!siteGuid || shift.siteGuid === siteGuid),
      ) ?? this.shiftDetails().find((shift) => format(shift.date, 'yyyy-MM-dd') === format(initialShiftDate, 'yyyy-MM-dd'));

      if (!matchingShift) {
        return;
      }

      this.selectedCalendarItemId.set(matchingShift.id);
      this.initialShiftContextApplied.set(true);
    });
  }

  onWorkspaceTabChanged(value: string): void {
    if (value) {
      this.workspaceTab.set(value);
      if (value === 'calendar' && !this.calendarVm().hasAnyItems && this.firstCalendarDate()) {
        this.calendarAnchor.set(startOfWeek(this.firstCalendarDate(), { weekStartsOn: 1 }));
      }
    }
  }

  onSchedulesChanged() {
    this.schedulesReload.update((value) => value + 1);
    this.selectedCalendarItemId.set(null);
  }

  async dismiss(result?: Record<string, unknown>): Promise<void> {
    await this.modalCtrl.dismiss(result);
  }

  async openSite(siteGuid: string | null): Promise<void> {
    if (!siteGuid) {
      return;
    }
    await this.dismiss();
    await this.navigationService.navigateToSite(siteGuid);
  }

  previousCalendarPeriod(): void {
    this.calendarAnchor.set(subWeeks(this.calendarAnchor(), 1));
    this.selectedCalendarItemId.set(null);
  }

  nextCalendarPeriod(): void {
    this.calendarAnchor.set(addWeeks(this.calendarAnchor(), 1));
    this.selectedCalendarItemId.set(null);
  }

  goToCurrentCalendarPeriod(): void {
    this.calendarAnchor.set(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
    this.selectedCalendarItemId.set(null);
  }

  selectCalendarItem(itemId: string): void {
    this.selectedCalendarItemId.set(itemId);
  }

  async addWorksiteLink(): Promise<void> {
    const availableSites = await firstValueFrom(this.availableWorksitesToLink$.pipe(take(1)));
    if (availableSites.length === 0) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: SelectDialogComponent<EmployeeWorksiteSelectItem>,
      componentProps: {
        items: availableSites.map((site) => ({ ...site })),
        multiple: true,
      },
      cssClass: ['stack-modal'],
    });
    await modal.present();

    const { data } = await modal.onDidDismiss<EmployeeWorksiteSelectItem[]>();
    const selected = data ?? [];
    if (!Array.isArray(selected) || selected.length === 0) {
      return;
    }

    for (const site of selected) {
await this.workforceApi.request('employee-site-links', 'POST', {firestore_user_id:this.user().guid, firestore_site_id:site.guid, enabled:true});
}
    this.worksiteLinksRefresh.update((value) => value + 1);
    this.userRefresh.update((value) => value + 1);
  }

  async removeWorksiteLink(site: EmployeeWorksiteLinkItem): Promise<void> {
    const confirmRet = await Dialog.confirm({
      title: this.translate.instant('general.delete'),
      message: this.translate.instant('users.workspace.sites.removeConfirm', { site: site.name }),
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    });

    if (!confirmRet.value) {
      return;
    }

    await this.workforceApi.request('employee-site-links', 'POST', {firestore_user_id:this.user().guid, firestore_site_id:site.guid, enabled:false});
    this.worksiteLinksRefresh.update((value) => value + 1);
    this.userRefresh.update((value) => value + 1);
  }

  getShiftStatus(date: Date): 'active' | 'upcoming' | 'ended' {
    const today = startOfToday();
    const shiftDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (shiftDate.getTime() === today.getTime()) {
      return 'active';
    }
    if (shiftDate.getTime() > today.getTime()) {
      return 'upcoming';
    }
    return 'ended';
  }

  getShiftStatusLabel(status: EmployeeShiftDetail['statusLabel']): string {
    switch (status) {
      case 'active':
        return this.translate.instant('users.status.active');
      case 'upcoming':
        return this.translate.instant('users.workspace.status.upcoming');
      case 'ended':
        return this.translate.instant('users.workspace.status.ended');
    }
  }

  private getPayrollStatusLabelText(status: EmployeePayrollPeriodSummary['status']): string {
    switch (status) {
      case 'ready':
        return this.translate.instant('users.workspace.payroll.status.ready');
      case 'pending':
        return this.translate.instant('users.workspace.payroll.status.pending');
      case 'incomplete':
        return this.translate.instant('users.workspace.payroll.status.incomplete');
    }
  }

  async addEmployeeDocument(documentKey = 'attachment'): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: AddAttachmentDialogComponent,
      componentProps: {
        document: documentKey,
        titleKey: 'users.workspace.documents.dialog.title',
      },
      cssClass: ['shell-modal', 'stack-modal'],
    });
    await modal.present();

    const { data } = await modal.onDidDismiss<IAttachment[]>();
    if (!data?.length) {
      return;
    }

    const attachments = [...this.user().attachments ?? []];
    data.forEach((attachment) => {
      const existingIndex = attachments.findIndex((item) => item.name === attachment.name);
      if (existingIndex >= 0) {
        attachments[existingIndex] = attachment;
      } else {
        attachments.push(attachment);
      }
    });

    await this.usersService.updateOnly(this.user().guid, { attachments });
    this.user().attachments = attachments;

    await this.feedback.showKey('users.workspace.documents.feedback.added', undefined, {
      color: 'success',
      duration: 2500,
    });
  }

  async openDocument(url: string | null): Promise<void> {
    if (!url) {
      return;
    }
    await Browser.open({ url });
  }

  async removeDocument(documentKey: string): Promise<void> {
    const attachment = this.user().attachments?.find((item) => item.name === documentKey);
    if (!attachment) {
      return;
    }

    const confirmRet = await Dialog.confirm({
      title: this.translate.instant('users.workspace.documents.feedback.removeTitle'),
      message: this.translate.instant('users.workspace.documents.feedback.removeMessage', {
        name: this.getDocumentDisplayName(documentKey, attachment),
      }),
    });

    if (!confirmRet.value) {
      return;
    }

    const attachments = (this.user().attachments ?? []).filter((item) => item.name !== documentKey);
    await this.usersService.updateOnly(this.user().guid, { attachments });
    this.user().attachments = attachments;

    await this.feedback.showKey('users.workspace.documents.feedback.removed', undefined, {
      color: 'success',
      duration: 2500,
    });
  }

  private findOverlaps(shifts: EmployeeShiftDetail[]): EmployeeShiftDetail[][] {
    const byDay = new Map<string, EmployeeShiftDetail[]>();
    shifts.forEach((shift) => {
      const key = format(shift.date, 'yyyy-MM-dd');
      const dayItems = byDay.get(key) ?? [];
      dayItems.push(shift);
      byDay.set(key, dayItems);
    });

    const overlaps: EmployeeShiftDetail[][] = [];
    byDay.forEach((items) => {
      const sorted = [...items].sort((a, b) => a.timeLabel.localeCompare(b.timeLabel));
      for (let i = 0; i < sorted.length - 1; i++) {
        const current = sorted[i];
        const next = sorted[i + 1];
        const currentEnd = this.parseShiftBoundary(current.date, current.timeLabel.split(' - ')[1]);
        const nextStart = this.parseShiftBoundary(next.date, next.timeLabel.split(' - ')[0]);
        if (currentEnd && nextStart && currentEnd > nextStart) {
          overlaps.push([current, next]);
        }
      }
    });
    return overlaps;
  }

  private parseShiftBoundary(date: Date, timeLabel?: string): Date | null {
    if (!timeLabel) {
      return null;
    }
    const [hours, minutes] = timeLabel.split(':').map((value) => Number.parseInt(value, 10));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
  }

  private mapPayrollPeriodSummary(period: OptionsPaycheckItem, timesheets: Timesheet[]): EmployeePayrollPeriodSummary {
    const totalHours = getDurations(timesheets) ?? 0;
    const approvedTimesheets = timesheets.filter((timesheet) => timesheet.checked || timesheet.status === 'checked' || timesheet.status === 'reported');
    const approvedHours = getDurations(approvedTimesheets) ?? 0;
    const missingEntries = timesheets.filter((timesheet) => !timesheet.checked || ['active', 'requested'].includes(timesheet.status)).length;
    const absences = timesheets.filter((timesheet) => !!timesheet.absence).length;
    const overtimeHours = timesheets.reduce((sum, timesheet) => {
      const over50 = timesheet.overtime50 ?? 0;
      const over100 = timesheet.overtime100 ?? 0;
      return sum + over50 + over100;
    }, 0);
    const estimatedPay = this.user()?.hourlyRate != null ? Math.round(totalHours * this.user().hourlyRate * 100) / 100 : null;

    let status: EmployeePayrollPeriodSummary['status'] = 'ready';
    if (missingEntries > 0) {
      status = 'pending';
    }
    if (totalHours === 0 && missingEntries === 0) {
      status = 'incomplete';
    }

    return {
      label: `${format(period.start, 'd.M.')} - ${format(period.end, 'd.M.yyyy')}`,
      start: period.start,
      end: period.end,
      totalHours,
      approvedHours,
      normalHours: Math.max(totalHours - overtimeHours, 0),
      estimatedPay,
      missingEntries,
      absences,
      overtimeHours,
      status,
      statusLabel: this.getPayrollStatusLabelText(status),
    };
  }

  private getEmploymentTypeLabel(value?: string | null): string {
    if (value === 'salary') {
      return this.translate.instant('users.workspace.settings.employment.salary');
    }
    if (value === 'invoice') {
      return this.translate.instant('users.workspace.settings.employment.invoice');
    }
    return this.translate.instant('users.workspace.settings.fallbacks.notDefined');
  }

  private getWorkerTypeLabel(value?: string | null): string {
    if (value === '40h') {
      return this.translate.instant('users.workspace.settings.workTypes.40h');
    }
    if (value === 'fullTime') {
      return this.translate.instant('users.workspace.settings.workTypes.fullTime');
    }
    if (value === 'noSelection') {
      return this.translate.instant('users.workspace.settings.fallbacks.notDefined');
    }
    return this.translate.instant('users.workspace.settings.fallbacks.notDefined');
  }

  private getCalendarTypeLabelByValues(type: EmployeeShiftDetail['calendarType'], absenceLabel: string | null): string {
    if (type === 'absence') {
      return absenceLabel || this.translate.instant('users.workspace.calendar.item.absence');
    }
    if (type === 'allocation') {
      return this.translate.instant('users.workspace.calendar.item.allocation');
    }
    return this.translate.instant('users.workspace.calendar.item.shift');
  }

  private mapDocumentItem(key: string, attachment?: IAttachment, required = false): EmployeeDocumentItem {
    return {
      key,
      name: this.getDocumentDisplayName(key, attachment),
      category: this.getDocumentCategory(key),
      uploadedDate: attachment
        ? this.translate.instant('users.workspace.documents.labels.uploaded')
        : this.translate.instant('users.workspace.documents.labels.notUploaded'),
      status: attachment?.fileUrl ? 'valid' : 'missing',
      uploader: attachment
        ? this.translate.instant('users.workspace.documents.labels.appUploader')
        : this.translate.instant('users.workspace.documents.labels.unknownUploader'),
      url: attachment?.fileUrl ?? null,
      required,
    };
  }

  private getDocumentDisplayName(key: string, attachment?: IAttachment): string {
    const translated = this.translate.instant('users.documents.' + key);
    if (translated && translated !== 'users.documents.' + key) {
      return translated;
    }
    return attachment?.nameText?.[this.translate.getCurrentLang() ?? 'fi'] ?? key;
  }

  private getDocumentCategory(key: string): EmployeeDocumentItem['category'] {
    if (['workSecurity', 'workingPermit'].includes(key)) {
      return 'certificates';
    }
    if (['passport', 'idCard'].includes(key)) {
      return 'identity';
    }
    if (key.toLowerCase().includes('contract') || key.toLowerCase().includes('agreement')) {
      return 'contracts';
    }
    return 'other';
  }

  async disableUser(): Promise<void> {
    const confirmRet = await Dialog.confirm({
      title: this.translate.instant('users.disableTitle'),
      message: this.translate.instant('users.disableText'),
    });

    if (!confirmRet.value) {
      return;
    }

    await this.usersService.updateOnly(this.user().guid, { role: Roles.registered, disabled: true });
    this.user().disabled = true;
    await this.feedback.showKey('general.saved', undefined, {
      color: 'success',
      duration: 3000,
    });
  }

  startBasicEdit(): void {
    const user = this.user();
    this.settingsError.set('');
    this.basicDraft.set({
      displayName: user.displayName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || '',
    });
    this.basicEdit.set(true);
  }

  cancelBasicEdit(): void {
    this.settingsError.set('');
    this.basicEdit.set(false);
  }

  startOrganizationEdit(teamLabel: string | null = this.teamLabelValue(), areaLabel: string | null = this.areaLabelValue()): void {
    this.settingsError.set('');
    this.organizationDraft.set({
      team: teamLabel || '',
      area: areaLabel || '',
      company: this.user().company || '',
    });
    this.organizationEdit.set(true);
  }

  cancelOrganizationEdit(): void {
    this.settingsError.set('');
    this.organizationEdit.set(false);
  }

  startEmploymentEdit(): void {
    const user = this.user();
    this.settingsError.set('');
    this.employmentDraft.set({
      salaryOrInvoice: user.salaryOrInvoice || '',
      workType: user.workType || '',
      hourlyRate: user.hourlyRate ?? null,
      sideCost: user.sideCost ?? null,
      tes: user.tes ?? '',
      paymentTerms: user.paymentTerms ?? '',
      inactive: !!user.inactive,
    });
    this.setHourlyRate24(user.hourlyRate ?? null);
    this.employmentEdit.set(true);
  }

  cancelEmploymentEdit(): void {
    this.settingsError.set('');
    this.hourlyRate24.set('');
    this.employmentEdit.set(false);
  }

  startExtraEdit(): void {
    const user = this.user();
    this.settingsError.set('');
    this.extraDraft.set({
      languages: user.languages?.join(', ') || '',
      street: user.address?.street || '',
      postalCode: user.address?.postalCode || '',
      city: user.address?.city || '',
    });
    this.extraEdit.set(true);
  }

  cancelExtraEdit(): void {
    this.settingsError.set('');
    this.extraEdit.set(false);
  }

  updateBasicDraft<K extends keyof BasicInfoDraft>(key: K, value: BasicInfoDraft[K]): void {
    this.basicDraft.update((draft) => ({ ...draft, [key]: value }));
  }

  updateOrganizationDraft<K extends keyof OrganizationDraft>(key: K, value: OrganizationDraft[K]): void {
    this.organizationDraft.update((draft) => ({ ...draft, [key]: value }));
  }

  updateEmploymentDraft<K extends keyof EmploymentDraft>(key: K, value: EmploymentDraft[K]): void {
    this.employmentDraft.update((draft) => ({ ...draft, [key]: value }));
    if (key === 'hourlyRate') {
      this.setHourlyRate24(value as EmploymentDraft['hourlyRate']);
    }
  }

  onEmploymentHourlyRate(value: string): void {
    const hourlyRate = value === '' ? null : Number(value);
    this.updateEmploymentDraft('hourlyRate', Number.isFinite(hourlyRate) ? hourlyRate : null);
  }

  updateExtraDraft<K extends keyof ExtraInfoDraft>(key: K, value: ExtraInfoDraft[K]): void {
    this.extraDraft.update((draft) => ({ ...draft, [key]: value }));
  }

  async onNetvisorEmployeeChange(value: string): Promise<void> {
    const netvisorEmployeeNumber = value || null;
    await this.runSettingsSave(async () => {
      await this.usersService.updateOnly(this.user().guid, { netvisorEmployeeNumber });
      this.integrationDraft.update((draft) => ({ ...draft, netvisorEmployeeNumber }));
      Object.assign(this.user(), { netvisorEmployeeNumber });
      this.userRefresh.update((current) => current + 1);
    });
  }

  async onProcountorEmployeeChange(value: string): Promise<void> {
    const parsed = value === '' ? null : Number(value);
    const procountorEmployeeNumber = Number.isFinite(parsed) ? parsed : null;
    await this.runSettingsSave(async () => {
      await this.usersService.updateOnly(this.user().guid, { procountorEmployeeNumber });
      this.integrationDraft.update((draft) => ({ ...draft, procountorEmployeeNumber }));
      Object.assign(this.user(), { procountorEmployeeNumber });
      this.userRefresh.update((current) => current + 1);
    });
  }

  async saveBasicInfo(): Promise<void> {
    const draft = this.basicDraft();
    const email = draft.email.trim();
    if (!draft.displayName.trim()) {
      this.settingsError.set(this.translate.instant('users.workspace.settings.validation.nameRequired'));
      return;
    }
    if (!email || !/.+@.+\..+/.test(email)) {
      this.settingsError.set(this.translate.instant('users.workspace.settings.validation.emailRequired'));
      return;
    }
    if (!draft.role) {
      this.settingsError.set(this.translate.instant('users.workspace.settings.validation.roleRequired'));
      return;
    }
    await this.runSettingsSave(async () => {
      await this.usersService.updateOnly(this.user().guid, {
        displayName: draft.displayName.trim(),
        email,
        phone: draft.phone.trim() || null,
        role: draft.role,
      });
      Object.assign(this.user(), {
        displayName: draft.displayName.trim(),
        email,
        phone: draft.phone.trim() || null,
        role: draft.role,
      });
      this.userRefresh.update((value) => value + 1);
      this.basicEdit.set(false);
    });
  }

  async saveOrganizationInfo(): Promise<void> {
    const draft = this.organizationDraft();
    await this.runSettingsSave(async () => {
      await this.usersService.updateOnly(this.user().guid, {
        company: draft.company.trim() || null,
      });
      Object.assign(this.user(), { company: draft.company.trim() || null });
      this.userRefresh.update((value) => value + 1);
      await this.saveTagSelection('team', draft.team);
      await this.saveTagSelection('area', draft.area);
      this.organizationEdit.set(false);
    });
  }

  async saveEmploymentInfo(): Promise<void> {
    const draft = this.employmentDraft();
    const isSalary = draft.salaryOrInvoice === 'salary';
    const isInvoice = draft.salaryOrInvoice === 'invoice';
    await this.runSettingsSave(async () => {
      await this.usersService.updateOnly(this.user().guid, {
        salaryOrInvoice: draft.salaryOrInvoice || null,
        workType: isSalary ? draft.workType || null : null,
        hourlyRate: draft.hourlyRate ?? null,
        sideCost: isSalary ? draft.sideCost ?? null : null,
        tes: isSalary ? draft.tes || null : null,
        paymentTerms: isInvoice ? draft.paymentTerms || null : null,
        inactive: draft.inactive,
        activeEnds: draft.inactive ? endOfToday() : null,
      });
      Object.assign(this.user(), {
        salaryOrInvoice: draft.salaryOrInvoice || null,
        workType: isSalary ? draft.workType || null : null,
        hourlyRate: draft.hourlyRate ?? null,
        sideCost: isSalary ? draft.sideCost ?? null : null,
        tes: isSalary ? draft.tes || null : null,
        paymentTerms: isInvoice ? draft.paymentTerms || null : null,
        inactive: draft.inactive,
        activeEnds: draft.inactive ? endOfToday() : null,
      });
      this.userRefresh.update((value) => value + 1);
      this.hourlyRate24.set('');
      this.employmentEdit.set(false);
    });
  }

  private setHourlyRate24(hourlyRate: number | null): void {
    if (!Number.isFinite(hourlyRate) || hourlyRate == null) {
      this.hourlyRate24.set('');
      return;
    }
    const value = hourlyRate * 1.24;
    this.hourlyRate24.set(value.toFixed(2));
  }

  async saveExtraInfo(): Promise<void> {
    const draft = this.extraDraft();
    await this.runSettingsSave(async () => {
      const languages = draft.languages
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const address = {
        street: draft.street.trim(),
        postalCode: draft.postalCode.trim(),
        city: draft.city.trim(),
      };
      const employee = this.user();
      const location = await resolveEmployeeCoordinates(employee, address,
        (street, postalCode, city) => this.addressGeocoder.getGeocodeStructured(street, postalCode, city));
      await this.usersService.updateOnly(employee.guid, { languages, address, location });
      Object.assign(employee, { languages, address, location });
      this.userRefresh.update((value) => value + 1);
      this.extraEdit.set(false);
    });
  }

  formatShiftTime(value?: Date | Timestamp): string {
    if (isTimestamp(value)) {
      return format(value.toDate(), 'HH:mm');
    }
    return value ? format(value, 'HH:mm') : '--:--';
  }

  formatValue(value?: string | null, emptyLabel = this.translate.instant('users.workspace.fallbacks.noDetails')): string {
    return value?.trim() || emptyLabel;
  }

  private getWorksiteGuids(user: User): string[] {
    const worksites = user?.worksites as unknown;
    if (!Array.isArray(worksites)) {
      return [];
    }
    return worksites
      .flatMap((item) => {
        if (typeof item === 'string') {
          return [item];
        }
        if (Array.isArray(item)) {
          return item
            .map((inner) => typeof inner === 'string'
              ? inner
              : typeof (inner as { guid?: unknown })?.guid === 'string'
                ? (inner as { guid: string }).guid
                : null)
            .filter((value): value is string => !!value);
        }
        if (typeof (item as { guid?: unknown })?.guid === 'string') {
          return [(item as { guid: string }).guid];
        }
        return [];
      })
      .filter((guid, index, arr) => guid.length > 0 && arr.indexOf(guid) === index);
  }

  private async runSettingsSave(action: () => Promise<void>): Promise<void> {
    try {
      this.settingsError.set('');
      await action();
      await this.feedback.showKey('general.saved', undefined, {
        color: 'success',
        duration: 2500,
      });
    } catch (error) {
      console.error('employee workspace settings save failed', error);
      this.settingsError.set(this.translate.instant('users.workspace.settings.validation.saveFailed'));
    }
  }

  private async saveTagSelection(type: 'team' | 'area', selectedName: string): Promise<void> {
    const tags = await firstValueFrom(this.contractorTags$.pipe(take(1)));
    const matchingTags = tags.filter((tag) => tag.type === type);
    const userItem = { guid: this.user().guid, name: this.user().displayName };

    await Promise.all(matchingTags.map(async (tag) => {
      const users = [...tag.users ?? []];
      const hasUser = users.some((tagUser) => tagUser.guid === this.user().guid);
      const shouldHaveUser = !!selectedName && tag.name === selectedName;

      if (shouldHaveUser && !hasUser) {
        tag.users = [...users, userItem];
        await this.tagsService.update(tag);
      } else if (!shouldHaveUser && hasUser) {
        tag.users = users.filter((tagUser) => tagUser.guid !== this.user().guid);
        await this.tagsService.update(tag);
      }
    }));
  }
}
